/**
 * Sync To Redux
 *
 * Helper to sync fetched projects data to the Redux store.
 * This maintains backward compatibility with existing Redux consumers.
 */

import type { AppDispatch, RootStore } from 'store';

import { setProjectsFromCache } from 'store/slices/projects';
import type { IProject } from 'types/project';

import type { FetchProjectsResult } from '../api/fetchAllProjectsWithPairs';

/**
 * Sync projects data to Redux store
 *
 * Dispatches the setProjectsFromCache action to update Redux state.
 * This allows existing selectors and consumers to work unchanged.
 *
 * @param dispatch - Redux dispatch function
 * @param data - Projects data to sync
 */
export const syncProjectsToRedux = (dispatch: AppDispatch, data: FetchProjectsResult): void => {
  dispatch(
    setProjectsFromCache({
      projects: data.projects,
      archivedProjects: data.archivedProjects,
    }),
  );
};

/**
 * Check if Redux store already has projects loaded
 *
 * @returns true if projects are already in Redux store
 */
export const isReduxProjectsLoaded = (state: RootStore): boolean => {
  return state.projects.projects !== null;
};

/**
 * Get current projects from Redux store
 *
 * @returns Current projects state or null if not loaded
 */
export const getReduxProjects = (
  state: RootStore,
): {
  projects: IProject[] | null;
  archivedProjects: IProject[] | null;
} => {
  return {
    projects: state.projects.projects,
    archivedProjects: state.projects.archivedProjects,
  };
};
